import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check, ChevronRight, Quote } from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";
import { FadeIn, MaskedLine, Eyebrow } from "@/components/Reveal";
import { Sparkle } from "@/components/Logo";
import IloveMeIcon from "@/components/IloveMeIcon";
import { ILOVEME_URL } from "@/lib/site";
import { SEGMENTS } from "@/lib/segments";

const BASE_URL = "https://www.contabeleza.com.br";

const EASE = [0.16, 1, 0.3, 1];

export default function SegmentPage({ config }) {
    const reduceMotion = useReducedMotion();
    const siblings = Object.values(SEGMENTS).filter((s) => s.slug !== config.slug);

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Início", item: `${BASE_URL}/` },
                    { "@type": "ListItem", position: 2, name: "Para quem", item: `${BASE_URL}/para-quem` },
                    { "@type": "ListItem", position: 3, name: config.nome, item: `${BASE_URL}/${config.slug}` },
                ],
            },
            {
                "@type": "Service",
                name: `Contabilidade para ${config.segmento}`,
                serviceType: "Contabilidade especializada para o mercado da beleza",
                provider: { "@type": "Organization", name: "Contabeleza", url: `${BASE_URL}/` },
                areaServed: "BR",
                description: config.description,
            },
            {
                "@type": "FAQPage",
                mainEntity: config.faq.map((item) => ({
                    "@type": "Question",
                    name: item.pergunta,
                    acceptedAnswer: { "@type": "Answer", text: item.resposta },
                })),
            },
        ],
    };

    return (
        <>
            <Seo title={config.title} description={config.description} jsonLd={jsonLd} />

            <section className="grain relative overflow-hidden bg-[#08104F] px-5 pb-16 pt-28 sm:px-8 sm:pt-36 lg:pb-24">
                <div className="pointer-events-none absolute inset-0 hero-grid-lines" />
                <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
                    <div>
                        <nav aria-label="Breadcrumb" className="mb-6">
                            <ol className="flex flex-wrap items-center gap-1.5 text-xs text-[#B2D0FF]/70">
                                <li>
                                    <Link to="/" className="transition-colors hover:text-white">
                                        Início
                                    </Link>
                                </li>
                                <li aria-hidden="true">
                                    <ChevronRight className="h-3 w-3" />
                                </li>
                                <li>
                                    <Link to="/para-quem" className="transition-colors hover:text-white">
                                        Para quem
                                    </Link>
                                </li>
                                <li aria-hidden="true">
                                    <ChevronRight className="h-3 w-3" />
                                </li>
                                <li aria-current="page" className="font-semibold text-[#F3EDE6]">
                                    {config.nome}
                                </li>
                            </ol>
                        </nav>

                        <FadeIn y={16}>
                            <p className="inline-flex items-center gap-2 rounded-full border border-[#B2D0FF]/25 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#B2D0FF]">
                                <Sparkle className="h-3 w-3" />
                                {config.eyebrow}
                            </p>
                        </FadeIn>

                        <h1 className="mt-6 font-display text-4xl leading-[1.08] tracking-tight text-[#F3EDE6] sm:text-5xl lg:text-[3.6rem]">
                            {config.h1Lines.map((line, i) => (
                                <MaskedLine key={line} delay={0.1 + i * 0.15}>
                                    {i === config.h1Lines.length - 1 ? (
                                        <span className="text-gradient-tricolor">{line}</span>
                                    ) : (
                                        line
                                    )}
                                </MaskedLine>
                            ))}
                        </h1>

                        <FadeIn delay={0.5} className="mt-6 max-w-xl">
                            <p className="text-base leading-relaxed text-[#B2D0FF] sm:text-lg">{config.subtitle}</p>
                        </FadeIn>

                        <FadeIn delay={0.65} className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <Link
                                to="/contato"
                                data-testid={`btn-segment-cta-${config.slug}`}
                                className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#0047BB] px-7 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#F3EDE6] hover:text-[#08104F] active:scale-[0.97]"
                            >
                                Solicitar diagnóstico gratuito
                                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                            <a
                                href="#faq-segmento"
                                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#B2D0FF]/30 px-7 py-4 text-sm font-semibold text-[#F3EDE6] transition-colors duration-200 hover:border-[#B2D0FF] hover:bg-white/5"
                            >
                                Perguntas frequentes
                            </a>
                        </FadeIn>
                    </div>

                    <motion.div
                        initial={reduceMotion ? false : { opacity: 0, scale: 0.94, rotate: 2 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
                        className="relative mx-auto w-full max-w-md"
                    >
                        <div className="gota-frame relative aspect-[4/3] border border-[#B2D0FF]/20 shadow-[0_40px_90px_rgba(0,0,0,0.45)]">
                            <img
                                src={config.imagem.src}
                                alt={config.imagem.alt}
                                width={config.imagem.width}
                                height={config.imagem.height}
                                className="h-full w-full object-cover"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#08104F]/45 via-transparent to-transparent" />
                        </div>
                        <Sparkle className="absolute -right-4 -top-4 h-8 w-8" />
                    </motion.div>
                </div>
            </section>

            <section className="px-5 py-16 sm:px-8 sm:py-24" aria-labelledby={`proposta-${config.slug}`}>
                <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
                    <FadeIn>
                        <Eyebrow>Nossa proposta</Eyebrow>
                        <h2
                            id={`proposta-${config.slug}`}
                            className="mt-4 font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl"
                        >
                            Contabilidade feita para {config.segmento}
                        </h2>
                    </FadeIn>
                    <FadeIn delay={0.12}>
                        <p className="text-lg leading-relaxed text-[#3A4768]">{config.proposta}</p>
                    </FadeIn>
                </div>
            </section>

            <section className="px-5 pb-16 sm:px-8 sm:pb-24" aria-label="Dores e benefícios">
                <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-2">
                    <FadeIn>
                        <div className="gota h-full rounded-[2rem] border border-[#F35E6A]/20 bg-[#F35E6A]/5 p-8 sm:p-10 lg:-rotate-1">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F35E6A]">
                                O que costuma travar
                            </p>
                            <ul className="mt-6 space-y-4">
                                {config.dores.map((dor, i) => (
                                    <li key={dor} className="flex gap-3">
                                        <span className="font-display text-lg text-[#F35E6A]/40">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <span className="text-sm leading-relaxed text-[#3A4768]">{dor}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.12}>
                        <div className="h-full rounded-[2rem] border border-[#0047BB]/20 bg-[#B2D0FF]/15 p-8 sm:p-10 lg:mt-10 lg:rotate-1">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0047BB]">
                                Como a Contabeleza destrava
                            </p>
                            <ul className="mt-6 space-y-4">
                                {config.beneficios.map((beneficio) => (
                                    <li key={beneficio} className="flex gap-3">
                                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0047BB]" />
                                        <span className="text-sm leading-relaxed text-[#3A4768]">{beneficio}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/solucoes"
                                className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0047BB]"
                            >
                                Ver todas as soluções
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </FadeIn>
                </div>
            </section>

            <section className="bg-[#FFFDFA] px-5 py-16 sm:px-8 sm:py-24" aria-labelledby={`servicos-${config.slug}`}>
                <div className="mx-auto max-w-7xl">
                    <FadeIn>
                        <Eyebrow>Serviços recomendados</Eyebrow>
                        <h2
                            id={`servicos-${config.slug}`}
                            className="mt-4 max-w-xl font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl"
                        >
                            O que costuma fazer diferença para {config.segmento}
                        </h2>
                    </FadeIn>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {config.servicos.map((servico, i) => (
                            <FadeIn key={servico} delay={i * 0.08}>
                                <Link
                                    to="/solucoes"
                                    className="group flex h-full flex-col justify-between rounded-3xl border border-[#08104F]/8 bg-[#F3EDE6] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#0047BB]/25 hover:shadow-[0_20px_50px_rgba(8,16,79,0.10)]"
                                >
                                    <span className="font-display text-3xl text-[#08104F]/10">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="mt-6 font-display text-base leading-snug text-[#08104F]">
                                        {servico}
                                    </span>
                                </Link>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {config.depoimentos.length > 0 && (
                <section className="px-5 py-16 sm:px-8 sm:py-24" aria-label="Depoimentos">
                    <div className="mx-auto max-w-7xl">
                        <FadeIn>
                            <Eyebrow>Quem vive essa rotina</Eyebrow>
                        </FadeIn>
                        <div className="mt-8 grid gap-5 md:grid-cols-2">
                            {config.depoimentos.map((t, i) => (
                                <FadeIn key={t.name} delay={i * 0.1}>
                                    <figure className="h-full rounded-3xl border border-[#08104F]/8 bg-[#FFFDFA] p-8">
                                        <Quote className="h-6 w-6 text-[#0047BB]/40" fill="currentColor" strokeWidth={0} />
                                        <blockquote className="mt-4 font-display text-lg leading-relaxed text-[#08104F]">
                                            {t.quote}
                                        </blockquote>
                                        <figcaption className="mt-6">
                                            <span className="block text-sm font-semibold text-[#08104F]">{t.name}</span>
                                            <span className="block text-xs text-[#3A4768]/75">{t.business}</span>
                                        </figcaption>
                                    </figure>
                                </FadeIn>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section
                id="faq-segmento"
                className="bg-[#FFFDFA] px-5 py-16 sm:px-8 sm:py-24"
                aria-labelledby={`faq-titulo-${config.slug}`}
            >
                <div className="mx-auto max-w-4xl">
                    <FadeIn className="text-center">
                        <Eyebrow>Perguntas frequentes</Eyebrow>
                        <h2
                            id={`faq-titulo-${config.slug}`}
                            className="mt-4 font-display text-3xl leading-[1.12] tracking-tight text-[#08104F] sm:text-4xl"
                        >
                            O que {config.segmento} mais perguntam
                        </h2>
                    </FadeIn>
                    <div className="mt-10 space-y-3">
                        {config.faq.map((item, i) => (
                            <FadeIn key={item.pergunta} delay={Math.min(i * 0.05, 0.2)}>
                                <details className="group rounded-2xl border border-[#08104F]/8 bg-[#F3EDE6] transition-colors open:border-[#0047BB]/25">
                                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 font-display text-base text-[#08104F] [&::-webkit-details-marker]:hidden">
                                        {item.pergunta}
                                        <ChevronRight className="h-4 w-4 shrink-0 text-[#0047BB] transition-transform duration-200 group-open:rotate-90" />
                                    </summary>
                                    <p className="px-5 pb-5 text-sm leading-relaxed text-[#3A4768]">{item.resposta}</p>
                                </details>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            <section className="px-5 py-14 sm:px-8" aria-label="Outros segmentos">
                <div className="mx-auto max-w-7xl">
                    <FadeIn>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#0047BB]">
                            Veja também outros segmentos
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            {siblings.map((s) => (
                                <Link
                                    key={s.slug}
                                    to={`/${s.slug}`}
                                    data-testid={`link-segment-${s.slug}`}
                                    className="group inline-flex items-center gap-2 rounded-full border border-[#08104F]/12 bg-[#FFFDFA] px-5 py-2.5 text-sm font-medium text-[#08104F] transition-all duration-200 hover:border-[#0047BB] hover:text-[#0047BB]"
                                >
                                    {s.nome}
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                                </Link>
                            ))}
                            <a
                                href={ILOVEME_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group inline-flex items-center gap-2 rounded-full bg-[#08104F] px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-[#0047BB]"
                            >
                                <IloveMeIcon className="inline-flex items-center gap-1" />
                                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                            </a>
                        </div>
                    </FadeIn>
                </div>
            </section>

            <CtaBand title={`Seu ${config.nome.toLowerCase()} merece uma gestão à altura.`} text={config.ctaTexto} />
        </>
    );
}
